import { useState } from 'react';
import { useAdmin } from '../../context/AdminContext';
import { useAuth0 } from '@auth0/auth0-react';
import { useAuth } from '../../AuthenticatedApp';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrash, faPlus, faCrown, faUserCog, faUsers } from '@fortawesome/free-solid-svg-icons';
import AddUserModal from '../modals/AddUserModal';
import EditUserModal from '../modals/EditUserModal';
import DeleteConfirmationModal from '../shared/DeleteConfirmationModal';
import AdminPageHeader from '../shared/AdminPageHeader';
import AdminMetricStrip from '../shared/AdminMetricStrip';
import AdminNotice from '../shared/AdminNotice';
import AdminEmptyState from '../shared/AdminEmptyState';
import AdminSectionCard from '../shared/AdminSectionCard';
import LoadingSpinner from '../LoadingSpinner';
import '../../styles/route_pages/Users.css';
import { addUser, deleteUser, updateUser } from '../../utils/authService';
import { useVenueId } from '../../hooks/useUserData';
import { errorTranslations } from '../../utils/errorMessages';

const Users = () => {
  const { users, setUsers, areas } = useAdmin();
  const { user, userRole, hasPermission } = useAuth();
  const venueId = useVenueId();
  const { getAccessTokenSilently } = useAuth0();
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [notice, setNotice] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all');
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  const handleUserAdded = async (newUser) => {
    try {
      setLoading(true);
      setNotice(null);
      const response = await addUser(venueId, { ...newUser, venueId }, getAccessTokenSilently);
      const addedUser = response.user || response;
      setUsers([...users, addedUser]);
      setIsAddModalVisible(false);
      setNotice({
        tone: 'success',
        title: 'Kullanıcı eklendi',
        message: `${addedUser.email} başarıyla eklendi.`,
      });
    } catch (error) {
      console.error('Error adding user:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleUserUpdated = async (updatedUser) => {
    try {
      setLoading(true);
      setNotice(null);
      const updated = await updateUser(venueId, { ...updatedUser, venueId }, getAccessTokenSilently);
      setUsers(users.map((listUser) => (listUser.id === updated.id ? updated : listUser)));
      setIsEditModalVisible(false);
      setNotice({
        tone: 'success',
        title: 'Kullanıcı güncellendi',
        message: `${updated.email} için değişiklikler kaydedildi.`,
      });
    } catch (error) {
      const translatedError = errorTranslations[error.message] || error.message;
      setNotice({
        tone: 'error',
        title: 'Kullanıcı güncellenemedi',
        message: translatedError,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!userToDelete) {
      return;
    }

    try {
      setLoading(true);
      setNotice(null);
      await deleteUser(venueId, userToDelete.id, getAccessTokenSilently);
      setUsers(users.filter((listUser) => listUser.id !== userToDelete.id));
      setShowDeleteConfirmation(false);
      setNotice({
        tone: 'success',
        title: 'Kullanıcı silindi',
        message: `${userToDelete.email} sistemden kaldırıldı.`,
      });
      setUserToDelete(null);
    } catch (error) {
      const translatedError = errorTranslations[error.message] || error.message;
      setNotice({
        tone: 'error',
        title: 'Kullanıcı silinemedi',
        message: translatedError,
      });
    } finally {
      setLoading(false);
    }
  };

  const isUserVisible = (listUser) => {
    if (userRole?.name === 'venue_admin' && listUser.role?.name === 'venue_owner') {
      return false;
    }

    return true;
  };

  const canManageUser = (targetUser) => {
    if (userRole?.name === 'venue_admin' && (targetUser.role?.name === 'venue_admin' || targetUser.role?.name === 'venue_owner')) {
      return false;
    }

    return true;
  };

  const getAreaName = (areaId) => {
    if (!areaId) {
      return 'Tüm alanlar';
    }

    const area = areas.find((entry) => entry.id === areaId);
    return area ? area.name : 'Atanmamış';
  };

  const getAreaBadgeClass = (areaId) => {
    if (!areaId) {
      return 'admin-chip admin-chip-warning';
    }

    const area = areas.find((entry) => entry.id === areaId);
    return `users-area-chip area-${area?.name?.toLowerCase() || 'default'}`;
  };

  const visibleUsers = users.filter((listUser) => !(userRole?.name === 'venue_admin' && listUser.role?.name === 'venue_owner'));

  const filteredUsers = users
    .filter((listUser) => {
      if (!isUserVisible(listUser)) {
        return false;
      }

      if (activeFilter === 'all') {
        return true;
      }

      return listUser.areaId === parseInt(activeFilter, 10);
    })
    .sort((a, b) => {
      const aIsCurrentUser = user?.email === a.email;
      const bIsCurrentUser = user?.email === b.email;

      if (aIsCurrentUser && !bIsCurrentUser) return -1;
      if (!aIsCurrentUser && bIsCurrentUser) return 1;
      return 0;
    });

  const metrics = [
    {
      key: 'all',
      label: 'Tüm Kullanıcılar',
      value: visibleUsers.length,
      helper: 'Rol görünürlüğü sonrası tüm ekip',
      active: activeFilter === 'all',
      onClick: () => setActiveFilter('all'),
    },
    ...areas.map((area) => {
      const areaCount = users.filter((listUser) => listUser.areaId === area.id && isUserVisible(listUser)).length;

      return {
        key: `area-${area.id}`,
        label: area.name,
        value: areaCount,
        helper: areaCount > 0 ? 'Bu alandaki ekip üyeleri' : 'Bu alan için kullanıcı yok',
        active: activeFilter === area.id.toString(),
        onClick: () => setActiveFilter(area.id.toString()),
      };
    }),
  ];

  return (
    <div className="page-container">
      <AdminPageHeader
        title="Kullanıcı Yönetimi"
        subtitle="Ekip üyelerini, erişim seviyelerini ve servis alanı atamalarını tek akışta yönetin."
        actions={
          hasPermission('manage_users') ? (
            <button className="btn btn-primary" onClick={() => setIsAddModalVisible(true)}>
              <FontAwesomeIcon icon={faPlus} />
              Yeni Kullanıcı
            </button>
          ) : null
        }
      />

      {notice ? (
        <AdminNotice
          tone={notice.tone}
          title={notice.title}
          message={notice.message}
          onDismiss={() => setNotice(null)}
          autoDismissMs={notice.tone === 'success' || notice.tone === 'info' ? 5000 : undefined}
        />
      ) : null}

      <AdminMetricStrip items={metrics} />

      <div className="admin-page-stack">
        <AdminSectionCard>
          {loading ? <LoadingSpinner /> : null}
          {filteredUsers.length === 0 ? (
            <AdminEmptyState
              icon={faUsers}
              title="Filtreye uygun kullanıcı yok"
              description="Seçili alanda gösterilecek ekip üyesi bulunmuyor. Filtreyi değiştirerek tüm ekibi görüntüleyebilirsiniz."
            />
          ) : (
            <div className={`admin-management-list users-management-list ${loading ? 'loading' : ''}`}>
              {filteredUsers.map((listUser) => {
                const isCurrentUser = user?.email === listUser.email;

                return (
                  <article key={listUser.id} className={`admin-management-row users-row ${isCurrentUser ? 'users-row-current' : ''}`}>
                    <div className="admin-management-leading">
                      <div className="admin-avatar-token users-avatar-token">
                        {listUser.email.charAt(0).toUpperCase()}
                        {isCurrentUser ? (
                          <span className="users-current-badge" aria-hidden="true">
                            <FontAwesomeIcon icon={faCrown} />
                          </span>
                        ) : null}
                      </div>
                      <div className="admin-management-main">
                        <h3 className="admin-management-title">
                          {listUser.email}
                          {isCurrentUser ? <span className="users-current-text"> Sen</span> : null}
                        </h3>
                        <div className="admin-chip-row">
                          <span className="admin-chip admin-chip-accent">{listUser.role?.name || 'Rol Yok'}</span>
                          <span className={`admin-chip ${getAreaBadgeClass(listUser.areaId)}`}>{getAreaName(listUser.areaId)}</span>
                          {isCurrentUser ? <span className="admin-chip admin-chip-success">Aktif Oturum</span> : null}
                        </div>
                      </div>
                    </div>

                    <div className="admin-management-meta">
                      <div className="admin-stat-stack">
                        <div className="admin-stat-card">
                          <strong>{getAreaName(listUser.areaId)}</strong>
                          <span>Servis kapsamı</span>
                        </div>
                        <div className="admin-stat-card">
                          <strong>{listUser.role?.name || 'Rol Yok'}</strong>
                          <span>Yetki seti</span>
                        </div>
                      </div>
                      {!hasPermission('manage_users') ? (
                        <div className="admin-muted-note">Bu listeyi görüntüleyebilirsiniz ancak kullanıcı yönetme yetkiniz yok.</div>
                      ) : null}
                      {hasPermission('manage_users') && !canManageUser(listUser) && !isCurrentUser ? (
                        <div className="admin-muted-note">{listUser.role?.name === 'venue_owner' ? 'Owner hesabı korumalıdır.' : 'Bu kullanıcı seviyesi düzenlenemez.'}</div>
                      ) : null}
                    </div>

                    <div className="admin-management-actions">
                      <div className="admin-action-cluster">
                        {hasPermission('manage_users') && !isCurrentUser && canManageUser(listUser) ? (
                          <>
                            <button type="button" className="btn btn-secondary" onClick={() => { setCurrentUser(listUser); setIsEditModalVisible(true); }}>
                              <FontAwesomeIcon icon={faEdit} />
                              Düzenle
                            </button>
                            <button type="button" className="btn btn-danger" onClick={() => { setNotice(null); setUserToDelete(listUser); setShowDeleteConfirmation(true); }}>
                              <FontAwesomeIcon icon={faTrash} />
                              Sil
                            </button>
                          </>
                        ) : null}
                        {isCurrentUser ? (
                          <Link to="/admin/profile" className="btn btn-secondary users-profile-link">
                            <FontAwesomeIcon icon={faUserCog} />
                            Profilime Git
                          </Link>
                        ) : null}
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </AdminSectionCard>
      </div>

      <AddUserModal show={isAddModalVisible} onClose={() => setIsAddModalVisible(false)} onUserAdded={handleUserAdded} />
      {currentUser ? (
        <EditUserModal
          show={isEditModalVisible}
          onClose={() => setIsEditModalVisible(false)}
          user={currentUser}
          onUserUpdated={handleUserUpdated}
        />
      ) : null}

      <DeleteConfirmationModal
        show={showDeleteConfirmation}
        onClose={() => setShowDeleteConfirmation(false)}
        onConfirm={handleConfirmDelete}
        title="Kullanıcıyı Sil"
        itemName={userToDelete?.email}
        message={`"${userToDelete?.email}" kullanıcısını silmek istediğinizden emin misiniz?`}
        consequences={['Kullanıcının sisteme erişimi tamamen kaldırılacak.', 'Bu kullanıcıya ait alan atamaları temizlenecek.']}
        loading={loading}
        type="danger"
      />
    </div>
  );
};

export default Users;
